/**
 * config/breach.js
 * Industry breach cost data and volume multipliers
 * Source: IBM Cost of Data Breach 2024
 */

// ── Breach Cost Engine v2 ────────────────────────────────────────────────────
// Source: IBM Cost of a Data Breach 2024 (primary)
// Supplementary: Verizon DBIR 2024, Ponemon, Gartner, Statista

// Industry base costs (IBM 2024)
export const INDUSTRY_COSTS = {
  healthcare:   { label: "Healthcare",              cost: 9770000,  source: "IBM 2024" },
  financial:    { label: "Financial Services",      cost: 6080000,  source: "IBM 2024" },
  industrial:   { label: "Industrial/Manufacturing",cost: 5560000,  source: "IBM 2024" },
  technology:   { label: "Technology / IT",         cost: 5450000,  source: "IBM 2024" },
  energy:       { label: "Energy / Critical Infra", cost: 5290000,  source: "IBM 2024" },
  education:    { label: "Education",               cost: 3650000,  source: "IBM 2024" },
  government:   { label: "Government / Public",     cost: 2600000,  source: "IBM 2024" },
  retail:       { label: "Retail / Hospitality",    cost: 3280000,  source: "IBM 2024" },
  global:       { label: "Global Average",          cost: 4880000,  source: "IBM 2024" },
};

// Volume multiplier — scales the industry base cost
export const getVolumeMultiplier = (attackCount) => {
  if (attackCount >= 100000) return { mult: 2.0,  label: "Large-scale breach (100K+ events)"   };
  if (attackCount >= 10000)  return { mult: 1.5,  label: "Sustained campaign (10K+ events)"    };
  if (attackCount >= 1000)   return { mult: 1.0,  label: "Full breach (1K+ events)"            };
  if (attackCount >= 100)    return { mult: 0.6,  label: "Significant incident (100+ events)"  };
  if (attackCount >= 10)     return { mult: 0.3,  label: "Targeted incident (10+ events)"      };
  return                            { mult: 0.1,  label: "Isolated incident (< 10 events)"     };
};

// Full cost breakdown — returns itemized components
export const estimateBreachCostBreakdown = (stats, industry = "global") => {
  if (!stats) return null;
  const attackCount = stats.critical + stats.high + stats.medium + stats.low;
  if (!attackCount) return null;

  const base    = (INDUSTRY_COSTS[industry] || INDUSTRY_COSTS.global).cost;
  const { mult, label: volumeLabel } = getVolumeMultiplier(attackCount);
  const total   = Math.round(base * mult);

  // IBM 2024 cost component breakdown (% of total breach cost)
  const detection      = Math.round(total * 0.29);  // 29% — Detection & escalation
  const notification   = Math.round(total * 0.076); // 7.6% — Notification
  const postBreach     = Math.round(total * 0.274); // 27.4% — Post-breach response
  const lostBusiness   = Math.round(total * 0.36);  // 36% — Lost business & downtime

  // Attack-type-specific cost components
  const irHours    = stats.critical > 0 ? 320 : stats.high > 0 ? 180 : 80; // hours
  const irRate     = 150; // $150/hr industry avg (SANS IR survey)
  const irCost     = irHours * irRate;

  // Downtime cost (Statista: avg $5,600/min enterprise downtime)
  const downtimeMin = stats.critical > 0 ? 180 : stats.high > 0 ? 60 : 15; // estimated minutes
  const downtimeCost = downtimeMin * 5600;

  // Patch & remediation (Ponemon: $18K–$45K per critical vuln)
  const patchCost = Math.min(
    (stats.critical > 0 ? 45000 : 18000) * Math.min(attackCount / 100, 5),
    225000
  );

  // Equipment/tools (Gartner: based on attack type)
  const equipCost = stats.critical > 0 ? 85000 : stats.high > 0 ? 45000 : 20000;

  // Regulatory fines (GDPR/HIPAA/PCI — if data exfiltration implied)
  const hasExfil = (stats.attackDist || []).some(a =>
    ["exfil","injection","sql","xss","web attack"].some(x => a.name.toLowerCase().includes(x))
  );
  const regulatoryFine = hasExfil ? Math.round(total * 0.08) : 0;

  return {
    total,
    industry: (INDUSTRY_COSTS[industry] || INDUSTRY_COSTS.global).label,
    volumeLabel,
    volumeMult:     mult,
    source:         "IBM Cost of Data Breach 2024 + Ponemon + Gartner + Statista",
    components: [
      {
        label:    "Detection & Investigation",
        cost:     detection,
        pct:      29,
        source:   "IBM 2024",
        note:     "Forensics, SIEM analysis, threat hunting, IR engagement",
        icon:     "🔍"
      },
      {
        label:    "Incident Response (Man Hours)",
        cost:     irCost,
        pct:      null,
        source:   "SANS IR Survey 2024",
        note:     `~${irHours} hrs × $150/hr avg IR rate`,
        icon:     "👥"
      },
      {
        label:    "Notification & Legal",
        cost:     notification,
        pct:      7.6,
        source:   "IBM 2024",
        note:     "Legal counsel, breach notification, credit monitoring setup",
        icon:     "⚖️"
      },
      {
        label:    "Post-Breach Response",
        cost:     postBreach,
        pct:      27.4,
        source:   "IBM 2024",
        note:     "Help desk, customer support, identity protection services",
        icon:     "🛡️"
      },
      {
        label:    "System Downtime",
        cost:     downtimeCost,
        pct:      null,
        source:   "Statista 2024",
        note:     `~${downtimeMin} min estimated downtime × $5,600/min enterprise avg`,
        icon:     "⏱️"
      },
      {
        label:    "Mitigation Equipment",
        cost:     equipCost,
        pct:      null,
        source:   "Gartner 2024",
        note:     "Firewall upgrades, IDS/IPS, WAF, network segmentation hardware",
        icon:     "🖥️"
      },
      {
        label:    "Software Solutions",
        cost:     Math.round(equipCost * 0.8),
        pct:      null,
        source:   "Gartner 2024",
        note:     "SIEM licenses, EDR/XDR, patch management, security tools",
        icon:     "💿"
      },
      {
        label:    "Patch & Remediation",
        cost:     patchCost,
        pct:      null,
        source:   "Ponemon 2024",
        note:     `$18K–$45K per critical vulnerability remediated`,
        icon:     "🔧"
      },
      {
        label:    "Lost Business & Reputation",
        cost:     lostBusiness,
        pct:      36,
        source:   "IBM 2024",
        note:     "Customer churn, reputational damage, business disruption",
        icon:     "📉"
      },
      ...(regulatoryFine > 0 ? [{
        label:    "Regulatory Fines",
        cost:     regulatoryFine,
        pct:      null,
        source:   "GDPR / HIPAA / PCI-DSS",
        note:     "Estimated regulatory penalties — data exfiltration indicators detected",
        icon:     "🏛️"
      }] : []),
    ],
  };
};

// Legacy simple estimate (used in executive tiles)
export const estimateBreachCost = (severityCounts, industry = "global") => {
  const base  = (INDUSTRY_COSTS[industry] || INDUSTRY_COSTS.global).cost;
  const total = (severityCounts.critical || 0) + (severityCounts.high || 0) +
                (severityCounts.medium   || 0) + (severityCounts.low  || 0);
  const { mult } = getVolumeMultiplier(total);
  return Math.round(base * mult);
};
